import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import {
  dummyColumnDef,
  MaterialIdLinkCellRenderer,
  specSectionLabel,
  specSectionRenderer,
  truncatedTextCellRenderer,
  userOrgCellRenderer
} from "components/cell-renders";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import {
  useIntegrationProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { getMaterialFormattedValues } from "pages/reports-menu/upcoming-workflows/export-csv-util";
import CiqAgSearch from "components/ciq-ag-search";
import { CiqIconBox } from "components/svg-icons";
import { RiskStates } from "../../constants";
import { useCiqFeatureMaterialRiskReportList } from "./hooks/risk-report";

type Props = {
  risk: RiskStates;
  isIntegrationMode: boolean;
};

function MaterialRiskDetail(props: Props) {
  const { risk, isIntegrationMode } = props;

  const { projectId } = useParams() as any;

  const gridRef = useRef<AgGridReact<any>>(null);

  const {
    projectDetails,
    columnHeaders: { materialHeaderMap }
  }: TProjectContext = useContext(ProjectContext);

  const { projectParticipants: WFProjectParticipants } =
    useProjectParticipants(isIntegrationMode);

  const { data: INTGProjectParticipants } = useIntegrationProjectParticipants(
    !isIntegrationMode
  );

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const { gridData } = useCiqFeatureMaterialRiskReportList({
    risk_level: risk
  });

  const customComparator = (valueA: any, valueB: any) => {
    return (valueA || "")
      .toLowerCase()
      .localeCompare((valueB || "").toLowerCase());
  };

  const defaultColDef: {} = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      sortingOrder: ["asc", "desc"],
      suppressColumnsToolPanel: false,
      flex: 1
    };
  }, []);
  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        colId: "material_id",
        field: "material_id",
        headerName: materialHeaderMap?.material_id || "",
        headerTooltip: materialHeaderMap?.material_id || "",
        filter: false,
        menuTabs: [],
        cellRenderer: MaterialIdLinkCellRenderer
      },
      {
        colId: "custom_spec_section",
        field: "custom_spec_section",
        headerName: materialHeaderMap?.spec_section_no || "",
        headerTooltip: materialHeaderMap?.spec_section_no || "",
        filter: "agSetColumnFilter",
        suppressHeaderMenuButton: false,
        menuTabs: ["filterMenuTab"],
        sortable: true,
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            try {
              return params.value.split("-")[0].trim();
            } catch {
              return params.value;
            }
          }
        },
        cellRenderer: (params: any) => {
          return specSectionRenderer(params, false);
        },
        cellRendererParams: {
          specNoField: "spec_section_no",
          specNameField: "spec_section_name"
        },
        valueGetter: (params: any) => {
          try {
            const specSectionStr = specSectionLabel({
              specNumber: params.data.spec_section_no,
              specName: params.data.spec_section_name
            });

            return specSectionStr;
          } catch (ex) {
            return "";
          }
        },
        comparator: customComparator
      },
      {
        field: "name",
        colId: "name",
        headerName: materialHeaderMap?.name || "",
        headerTooltip: materialHeaderMap?.name || "",
        filter: false,
        menuTabs: [],
        valueGetter: (params: any) => {
          const { data } = params;
          if (
            !data.material_id &&
            data.material_submitttal_links &&
            data.material_submitttal_links.length
          ) {
            const submittalId = data.material_submitttal_links[0].submittal_id;
            const specNo = data.spec_section_no;

            let materialName = "Material for Submittal ";
            if (specNo) {
              materialName += `${specNo} - `;
            }
            if (submittalId) {
              materialName += `${submittalId}`;
            }
            return materialName;
          }

          return data.name;
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "material_tag",
        colId: "material_tag",
        headerName: materialHeaderMap?.tag_name || "",
        headerTooltip: materialHeaderMap?.tag_name || "",
        menuTabs: [],
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "trade_partner_name",
        colId: "trade_partner",
        headerName: materialHeaderMap?.trade_partner || "",
        headerTooltip: materialHeaderMap?.trade_partner || "",
        sort: "asc",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "assignee",
        headerName: materialHeaderMap?.assignee || "",
        headerTooltip: materialHeaderMap?.assignee || "",
        cellRenderer: (params: any) => {
          const data = userOrgCellRenderer(params, false);
          return data.cellMarkup;
        },
        cellRendererParams: {
          userInput: {
            id: "assignee",
            firstName: "assignee_first_name",
            lastName: "assignee_last_name",
            statusKey: "assignee_status"
          }
        },
        valueGetter: (params: any) => {
          const data = userOrgCellRenderer(params, false);
          return data.fullName;
        }
      },
      {
        field: "status",
        colId: "status",
        headerName: materialHeaderMap?.status || "",
        headerTooltip: materialHeaderMap?.status || "",
        valueGetter: ({ data }) => {
          if (
            data?.date_block_materials?.length > 0 &&
            data?.date_block_materials[0]?.milestone_state
          ) {
            return data?.date_block_materials[0]?.milestone_state || "";
          }
          return "";
        },
        menuTabs: ["filterMenuTab"],
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: "RISK",
        hide: true
      },
      {
        colId: "risk_assessment",
        headerName: "RISK ASSESSMENT",
        hide: true
      },
      {
        colId: "linked_submittals",
        field: "material_submitttal_links",
        headerName: "LINKED SUBMITTALS",
        hide: true
      },
      {
        colId: "linked_tasks",
        field: "material_schedule_links",
        headerName: "IMPACTED ACTIVITIES",
        hide: true
      }
    ];
  }, [materialHeaderMap]);

  useEffect(() => {
    if (gridRef.current && gridData.materials) {
      setTimeout(() => {
        gridRef.current?.api.refreshCells({
          force: true,
          columns: ["assignee"]
        });
      }, 0);
    }
  }, [gridData.materials]);

  const gridContext: any = useMemo(() => {
    return {
      projectId,
      isIntegrationMode,
      projectParticipants
    };
  }, [isIntegrationMode, projectParticipants, projectId]);

  const exportLogAsCSV = () => {
    const additionalColumns = [
      "risk_level",
      "risk_assessment",
      "linked_submittals",
      "linked_tasks"
    ];
    const api = gridRef.current?.api;
    if (gridRef.current && api)
      gridRef.current?.api?.exportDataAsCsv({
        columnKeys: api
          .getAllGridColumns()
          ?.filter((col: any) => {
            const colId = col.getColId();
            return col.isVisible() || additionalColumns?.includes(colId);
          })
          .map((col: any) => col.getColId()),
        processCellCallback(params: any): string {
          return getMaterialFormattedValues(params);
        },
        fileName: `Materials at ${risk} Risk_${projectDetails?.name}.csv`
      });
  };

  return (
    <div>
      <div className="pb-3 pt-5 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CiqIconBox feature="MATERIALS" />
          <div className="flex text-base font-semibold">
            Materials at <div className="camelCase px-1">{risk}</div> Risk
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <CiqAgSearch
            key="RiskMaterial_search"
            gridRef={gridRef}
            placeholder="Search Materials"
          />

          <Button
            disabled={!gridData.materials || !gridData.materials.length}
            className="w-[110px] hide-on-print"
            onClick={exportLogAsCSV}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <div style={{ height: "calc(100vh - 325px)" }}>
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={gridData.materials}
          headerHeight={36}
          rowHeight={62}
          pagination={false}
          suppressPaginationPanel
          suppressContextMenu
          context={gridContext}
          tooltipShowMode="whenTruncated"
        />
      </div>
    </div>
  );
}

export default MaterialRiskDetail;
